import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { EditPositionDrawer } from '../components/domain/EditPositionDrawer';
import { EditStopDialog } from '../components/domain/EditStopDialog';
import { PositionsTable } from '../components/domain/PositionsTable';
import { PricesAsOf } from '../components/domain/PricesAsOf';
import { TopStatusBar } from '../components/domain/TopStatusBar';
import { TradingNextActionsCard } from '../components/domain/TradingNextActionsCard';
import { TradingPortfolioHeatCard } from '../components/domain/TradingPortfolioHeatCard';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { usePrimaryAccount } from '../features/accounts/queries';
import { useMe } from '../features/auth/queries';
import { useRecomputePositions } from '../features/imports/mutations';
import {
  usePositionPriceHistories,
  usePositions,
  useTradingPortfolioHeat,
  useTradingNextActions,
} from '../features/positions/queries';
import { useLatestSnapshot } from '../features/snapshots/queries';
import type { PositionDto } from '../lib/api/endpoints';

export function Positions() {
  const navigate = useNavigate();
  const me = useMe();
  const { accounts, account: primaryAccount } = usePrimaryAccount();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const activeAccountId = selectedAccountId ?? primaryAccount?.id ?? null;
  const activeAccount =
    accounts.data?.find((account) => account.id === activeAccountId) ??
    (primaryAccount?.id === activeAccountId ? primaryAccount : null);
  const latestSnapshot = useLatestSnapshot(activeAccountId);
  const positions = usePositions(activeAccountId);
  const tradingNextActions = useTradingNextActions(
    activeAccountId,
    activeAccount?.mandate === 'SWING_TRADING',
  );
  const tradingPortfolioHeat = useTradingPortfolioHeat(
    activeAccountId,
    activeAccount?.mandate === 'SWING_TRADING',
  );
  const recomputePositions = useRecomputePositions();
  const completePositions = positions.data?.positions ?? [];
  const historyIncompletePositions = positions.data?.historyIncomplete ?? [];
  const allPositions = [...completePositions, ...historyIncompletePositions];
  const indicatorHistoryBySymbol = usePositionPriceHistories(
    activeAccountId,
    allPositions.map((position) => position.symbol),
  );
  const [editingPosition, setEditingPosition] = useState<PositionDto | null>(null);
  const [editingStopPosition, setEditingStopPosition] = useState<PositionDto | null>(null);

  if (me.isLoading || (accounts.isLoading && !primaryAccount)) {
    return <main className="p-6 text-text-muted">Loading positions...</main>;
  }

  if (me.isError || accounts.isError || !me.data) {
    return <main className="p-6 text-danger">Could not load positions.</main>;
  }

  return (
    <main className="min-h-screen bg-bg text-text">
      <TopStatusBar currency={me.data.baseCurrency} snapshot={latestSnapshot.data} />
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">Positions</h1>
            <p className="mt-1 text-sm text-text-muted">
              Open risk, stops, days held, and traffic-light status flags.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {accounts.data && accounts.data.length > 0 ? (
              <label className="text-sm text-text-muted">
                Account
                <select
                  className="mt-1 block w-full rounded border border-border bg-bg-muted px-3 py-2 text-text sm:w-56"
                  value={activeAccountId ?? ''}
                  onChange={(event) => setSelectedAccountId(event.target.value)}
                >
                  {accounts.data.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              disabled={!activeAccountId || recomputePositions.isPending}
              onClick={() => {
                if (!activeAccountId) return;
                if (
                  allPositions.some((position) => position.isManualOverride) &&
                  !window.confirm('Recomputing will remove all manual overrides. Continue?')
                ) {
                  return;
                }
                recomputePositions.mutate(activeAccountId, {
                  onSuccess: (result) => {
                    toast.success(`Recomputed ${result.positionsRecomputed} positions.`);
                  },
                });
              }}
            >
              {recomputePositions.isPending ? 'Recomputing...' : 'Recompute positions'}
            </Button>
            <Button asChild variant="secondary">
              <Link to="/import">Import</Link>
            </Button>
          </div>
        </header>

        {activeAccount?.mandate === 'SWING_TRADING' && activeAccountId ? (
          <>
            <TradingPortfolioHeatCard
              heat={tradingPortfolioHeat.data}
              isError={tradingPortfolioHeat.isError}
              isLoading={tradingPortfolioHeat.isLoading}
            />
            <TradingNextActionsCard
              accountId={activeAccountId}
              actions={tradingNextActions.data ?? []}
              isError={tradingNextActions.isError}
              isLoading={tradingNextActions.isLoading}
              onOpenStopEditor={(symbol) => {
                const position = allPositions.find((candidate) => candidate.symbol === symbol);
                if (position) setEditingStopPosition(position);
              }}
            />
          </>
        ) : null}

        <Card
          header={
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold">Open positions</h2>
                <p className="text-sm text-text-muted">
                  {activeAccount
                    ? `${activeAccount.name} | ${activeAccount.currency}`
                    : 'Create an account and import trades to begin.'}
                </p>
              </div>
              {positions.data ? (
                <PricesAsOf positions={allPositions} refreshing={positions.isFetching} />
              ) : null}
            </div>
          }
        >
          {!activeAccount ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-text-muted">No accounts exist yet.</p>
              <Button asChild variant="secondary">
                <Link to="/settings">Open settings</Link>
              </Button>
            </div>
          ) : positions.isLoading ? (
            <p className="text-sm text-text-muted">Loading open positions...</p>
          ) : positions.isError ? (
            <p className="text-sm text-danger">Could not load open positions.</p>
          ) : allPositions.length > 0 ? (
            <PositionsTable
              hasSnapshot={Boolean(latestSnapshot.data)}
              positions={completePositions}
              historyIncomplete={historyIncompletePositions}
              indicatorHistoryBySymbol={indicatorHistoryBySymbol}
              onEditPosition={setEditingPosition}
              onOpenDetail={(symbol) => {
                navigate(`/positions/${symbol}?accountId=${activeAccountId ?? ''}`);
              }}
            />
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-text-muted">No open positions for this account.</p>
              <Button asChild variant="secondary">
                <Link to="/import">Import trades</Link>
              </Button>
            </div>
          )}
        </Card>
      </div>
      <EditPositionDrawer
        position={editingPosition}
        onOpenChange={(open) => {
          if (!open) setEditingPosition(null);
        }}
      />
      {editingStopPosition ? (
        <EditStopDialog
          open
          position={editingStopPosition}
          onOpenChange={(open) => {
            if (!open) setEditingStopPosition(null);
          }}
        />
      ) : null}
    </main>
  );
}
